/**
 * Shared Chart.js setup for the finance charts.
 *
 * Colours come from the live CSS custom properties rather than hard-coded hex,
 * so theme and accent switches propagate without re-rendering the chart data.
 */
import {
  Chart,
  BarController, BarElement,
  LineController, LineElement, PointElement,
  DoughnutController, ArcElement,
  CategoryScale, LinearScale,
  Filler, Legend, Tooltip,
} from 'chart.js';

let registered = false;

/** Register only the controllers we use — keeps the bundle honest. */
export function registerCharts() {
  if (registered) return;
  Chart.register(
    BarController, BarElement,
    LineController, LineElement, PointElement,
    DoughnutController, ArcElement,
    CategoryScale, LinearScale,
    Filler, Legend, Tooltip,
  );
  registered = true;
}

export function cssVar(name: string, fallback = '#888'): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export const chartColours = () => ({
  text: cssVar('--vn-text-primary', '#ececf0'),
  muted: cssVar('--vn-text-muted', '#4c4c58'),
  secondary: cssVar('--vn-text-secondary', '#7a7a86'),
  grid: 'rgba(255,255,255,0.05)',
  border: cssVar('--vn-border-default', '#1e1e24'),
  card: cssVar('--vn-surface-card', '#131316'),
  success: cssVar('--vn-success', '#22c55e'),
  danger: cssVar('--vn-danger', '#ef4444'),
  warning: cssVar('--vn-warning', '#f59e0b'),
  info: cssVar('--vn-info', '#3b82f6'),
  violet: cssVar('--vn-accent-violet', '#8b5cf6'),
  accent: cssVar('--vn-accent-safe', '#22c55e'),
});

export const PALETTE = [
  '#3b82f6', '#22c55e', '#8b5cf6', '#f472b6', '#f59e0b',
  '#ef4444', '#06b6d4', '#facc15', '#84cc16', '#ec4899',
  '#a78bfa', '#10b981', '#fb923c', '#60a5fa',
];

export const MONO = "var(--vn-font-mono, ui-monospace), monospace";

/**
 * Privacy-mask stamp. Chart.js paints its figures (axis ticks, tooltips) at
 * build time, so masking amounts app-wide has to force a rebuild — the
 * formatters the app passes in are mask-aware at call time, they just need to
 * be *called again*. The app bumps this on every mask toggle and `useChart`
 * folds it into its deps.
 */
let maskStamp = 0;
export function bumpChartMask(): void { maskStamp++; }
export function chartMaskStamp(): number { return maskStamp; }

/** Device pixel ratio, adjusted for any CSS `zoom` applied to the document. */
export function canvasPixelRatio(): number {
  if (typeof window === 'undefined') return 1;
  const zoom = Number(document.documentElement.style.zoom) || 1;
  return (window.devicePixelRatio || 1) * zoom;
}

/** Options every chart shares — dark tooltips, tabular ticks, no aspect lock. */
export function baseOptions(fmt: (v: number) => string) {
  const c = chartColours();
  return {
    responsive: true,
    maintainAspectRatio: false,
    /*
     * Fold the interface scale into the backing-store resolution.
     *
     * CSS `zoom` on <html> enlarges the canvas element without telling Chart.js,
     * so it keeps rendering at 1 device pixel per unzoomed CSS pixel and the
     * result is soft. Multiplying the ratio by the zoom restores it. SVG charts
     * don't need this; canvas ones do.
     */
    devicePixelRatio: canvasPixelRatio(),
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: c.secondary,
          font: { family: MONO, size: 10 },
          boxWidth: 10,
          padding: 9,
          usePointStyle: true,
          pointStyle: 'circle' as const,
        },
      },
      tooltip: {
        backgroundColor: c.card,
        borderColor: c.border,
        borderWidth: 1,
        titleColor: c.text,
        bodyColor: c.text,
        padding: 9,
        titleFont: { family: 'var(--vn-font-display)', size: 12 },
        bodyFont: { family: MONO, size: 11 },
        callbacks: {
          label(ctx: any) {
            const raw = ctx.raw;
            const v = Array.isArray(raw) ? raw[1] - raw[0] : ctx.parsed.y ?? ctx.parsed;
            return `${ctx.dataset.label ?? ctx.label}: ${fmt(v)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { color: c.grid, drawTicks: false },
        border: { color: c.border },
        ticks: { color: c.muted, font: { family: MONO, size: 9 }, autoSkip: true, maxTicksLimit: 8 },
      },
      y: {
        grid: { color: c.grid, drawTicks: false },
        border: { display: false },
        ticks: { color: c.muted, font: { family: MONO, size: 9 }, callback: (v: any) => fmt(Number(v)) },
      },
    },
  };
}

export const compactMoney = (v: number) =>
  (v < 0 ? '−' : '') + '$' + (Math.abs(v) >= 1000
    ? Math.round(Math.abs(v) / 1000) + 'k'
    : Math.round(Math.abs(v)).toString());
